import { useEffect, useRef } from 'react';
import {
  holdBackForBlockingGate,
  takeOverBackForLayer,
  takeOverBackOnScreen,
} from 'Utils/androidBackButton';

/**
 * Hands a screen's own Back to Android's hardware or gesture Back while the screen is mounted
 * (see `takeOverBackOnScreen`), so hardware Back does what the screen's on-screen Back does. The
 * handler is read when Back is pressed, so it can change between renders.
 */
export const useHardwareBack = (pathname: string, handleBack: () => void): void => {
  const handleBackRef = useRef(handleBack);
  handleBackRef.current = handleBack;
  useEffect(() => takeOverBackOnScreen(pathname, () => handleBackRef.current()), [pathname]);
};

/**
 * Hands the close of a dialog, menu or panel to Android's hardware or gesture Back while it is open
 * (see `takeOverBackForLayer`), so Back closes it first. `close` must close, not toggle: it is what
 * the layer's own X, Cancel or tap outside does. Read when Back is pressed. `stackLevel` is the
 * z-index the layer is drawn at.
 */
export const useHardwareBackClosesLayer = (
  open: boolean,
  close: () => void,
  stackLevel: number,
): void => {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(
    () => (open ? takeOverBackForLayer(() => closeRef.current(), stackLevel) : undefined),
    [open, stackLevel],
  );
};

/**
 * Holds Android's hardware or gesture Back while a screen the user cannot close is shown (see
 * `holdBackForBlockingGate`): Back sends the app to the background and nothing beneath it runs (no
 * screen's Back, no layer's close, no history). Once the screen goes away Back is as before.
 */
export const useBlockingGateHoldsBack = (shown: boolean): void => {
  useEffect(() => (shown ? holdBackForBlockingGate() : undefined), [shown]);
};
